/*
Copyright 2026 Integrated Tech9labs Pvt. Ltd.
All Rights Reserved.

This file is part of Swift and is licensed under the GNU Affero General
Public License, version 3 (see the LICENSE file at the root of the
repository).
*/

/**
 * DR alerts (DR Phases 25-26): what needs someone's attention in this client, raised and cleared by Swift's DR
 * monitor and also sent to the client's Teams and email (swift-events). People acknowledge them; an RTO alert is
 * cleared when acknowledged. Each client sets the thresholds and mutes kinds in its DR settings ("alerts").
 */

import * as React from "react";
import styled from "styled-components";
import { Check, FileText } from "lucide-react";

import { drErrorMessage } from "@src/components/modules/DrModule/drUtils";
import { ActionButton } from "@src/components/ui/DetailsKit";
import { Muted, Pill, T9, formatDateTime, timeAgo } from "@src/components/ui/Lists/ListTable";
import { Notice, OpsInput } from "@src/components/ui/OpsDialog";
import drReportSource from "@src/sources/DrReportSource";
import drSource from "@src/sources/DrSource";

import type { DrAlert } from "@src/@types/Dr";

export const ALERT_LABELS: { [kind: string]: string } = {
  RPO_BREACH: "RPO missed",
  REPLICATION_FAILED: "Replication failed",
  REPLICATION_DEGRADED: "Replication degraded",
  REPLICATION_PAUSED: "Replication paused",
  RUN_FAILED: "DR run failed",
  APPROVAL_WAITING: "Approval waiting",
  REVIEW_PENDING: "Emergency failover to review",
  RTO_EXCEEDED: "RTO missed",
  DRILL_OVERDUE: "Test failover overdue",
};

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;
const Item = styled.li`
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr) auto;
  gap: 4px 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${T9.slate100};
  font-size: 13px;
  color: ${T9.slate700};
  align-items: start;
`;
const Title = styled.div`
  font-weight: 600;
  color: ${T9.slate900};
`;
const Ack = styled.div`
  grid-column: 2 / 4;
  display: flex;
  gap: 8px;
  align-items: center;
`;

type Props = {
  /** Called when the number of active alerts changes (the tab's count). */
  onCount?: (active: number) => void;
};
const DrAlertsView = (props: Props) => {
  const [alerts, setAlerts] = React.useState<DrAlert[] | null>(null);
  const [all, setAll] = React.useState(false);
  const [acking, setAcking] = React.useState<string | null>(null);
  const [comment, setComment] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [days, setDays] = React.useState(30);
  const [reporting, setReporting] = React.useState(false);
  const { onCount } = props;

  const report = async () => {
    setReporting(true);
    setError(null);
    try {
      await drReportSource.downloadServiceReport(days);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setReporting(false);
    }
  };

  const load = React.useCallback(async () => {
    try {
      const found = await drSource.alerts(all);
      setAlerts(found);
      onCount?.(found.filter(a => a.active).length);
    } catch (err) {
      setError(drErrorMessage(err, "Could not load the alerts."));
    }
  }, [all, onCount]);
  React.useEffect(() => {
    load();
  }, [load]);

  const acknowledge = async (alert: DrAlert) => {
    setError(null);
    try {
      await drSource.acknowledgeAlert(alert.id, comment.trim() || undefined);
      setAcking(null);
      setComment("");
      await load();
    } catch (err) {
      setError(drErrorMessage(err, "SwiftIQ did not record the acknowledgment."));
    }
  };

  if (!alerts) return error ? <Notice tone="danger">{error}</Notice> : <Muted>Loading…</Muted>;
  return (
    <div data-test-id="dr-alerts" style={{ display: "flex", flexDirection: "column", gap: 10, padding: 16 }}>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ fontSize: 13, display: "flex", gap: 6, alignItems: "center" }}>
          <input type="checkbox" checked={all} onChange={e => setAll(e.target.checked)} data-test-id="dr-alerts-all" />
          Show cleared alerts too
        </label>
        <span style={{ flex: 1 }} />
        <select value={days} onChange={e => setDays(Number(e.target.value))} data-test-id="dr-service-report-days" aria-label="Report period">
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 12 months</option>
        </select>
        <ActionButton type="button" variant="secondary" onClick={report} disabled={reporting} data-test-id="dr-service-report">
          <FileText size={16} /> {reporting ? "Building…" : "Service report (PDF)"}
        </ActionButton>
      </div>
      {alerts.length === 0 ? (
        <Muted>{all ? "No alerts yet." : "Nothing needs attention."}</Muted>
      ) : (
        <List>
          {alerts.map(a => (
            <Item key={a.id} data-test-id="dr-alert">
              <div>
                {!a.active ? (
                  <Pill tone="neutral">Cleared</Pill>
                ) : (
                  <Pill tone={a.severity === "critical" ? "danger" : "warning"}>{a.severity === "critical" ? "Critical" : "Warning"}</Pill>
                )}
              </div>
              <div>
                <Title>{ALERT_LABELS[a.kind] || a.kind}</Title>
                <div>{a.detail.message}</div>
                <Muted>
                  Raised <span title={formatDateTime(a.raised_at)}>{timeAgo(a.raised_at)}</span>
                  {a.cleared_at ? <> · cleared <span title={formatDateTime(a.cleared_at)}>{timeAgo(a.cleared_at)}</span></> : null}
                  {a.detail.acknowledged_by
                    ? ` · acknowledged by ${a.detail.acknowledged_by}${a.detail.acknowledged_comment ? `: ${a.detail.acknowledged_comment}` : ""}`
                    : ""}
                </Muted>
              </div>
              <div>
                {a.active && !a.detail.acknowledged_by ? (
                  <ActionButton
                    type="button"
                    variant="secondary"
                    onClick={() => setAcking(acking === a.id ? null : a.id)}
                    data-test-id="dr-alert-ack"
                  >
                    <Check size={16} /> Acknowledge…
                  </ActionButton>
                ) : null}
              </div>
              {acking === a.id ? (
                <Ack>
                  <OpsInput
                    value={comment}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setComment(e.target.value)}
                    placeholder="What you are doing about it (kept in the audit log)"
                    style={{ flex: 1 }}
                    data-test-id="dr-alert-ack-comment"
                  />
                  <ActionButton type="button" onClick={() => acknowledge(a)} data-test-id="dr-alert-ack-confirm">
                    Acknowledge
                  </ActionButton>
                </Ack>
              ) : null}
            </Item>
          ))}
        </List>
      )}
    </div>
  );
};

export default DrAlertsView;
